import {
  type CountryCode,
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
} from 'libphonenumber-js';

/** Canonical vanilla-TS phone input for the donation widget.
 *
 * The organization country is an initial default only. Donors can select any
 * supported country; a leading `+` is always parsed internationally. Successful
 * parsing returns E.164 so no national-format value reaches an API payload.
 */

const SUPPORTED_COUNTRIES = new Set<CountryCode>(getCountries());
const COUNTRY_LABEL_LOCALE = 'en';

function esc(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function resolvePhoneCountry(
  candidate: string | null | undefined,
  fallback: CountryCode = 'US',
): CountryCode {
  const normalized = String(candidate || '').trim().toUpperCase() as CountryCode;
  return SUPPORTED_COUNTRIES.has(normalized) ? normalized : fallback;
}

export function localePhoneCountry(locale?: string | null): CountryCode {
  const match = String(locale || '').replace('_', '-').match(/-([A-Za-z]{2})(?:-|$)/);
  return resolvePhoneCountry(match?.[1], 'US');
}

export interface NormalizedPhoneInput {
  e164: string;
  country: CountryCode;
}

export function normalizePhoneInput(
  raw: string,
  selectedCountry: CountryCode,
): NormalizedPhoneInput | null {
  const value = raw.trim();
  if (!value) return null;
  try {
    const parsed = value.startsWith('+')
      ? parsePhoneNumberFromString(value)
      : parsePhoneNumberFromString(value, selectedCountry);
    if (!parsed || !parsed.isValid()) return null;
    return {
      e164: parsed.number,
      country: parsed.country || selectedCountry,
    };
  } catch {
    return null;
  }
}

export function countryDisplayName(country: CountryCode, locale = 'en'): string {
  try {
    const DisplayNames = (Intl as typeof Intl & {
      DisplayNames?: new (locales?: string | string[], options?: { type: 'region' }) => {
        of(code: string): string | undefined;
      };
    }).DisplayNames;
    return DisplayNames
      ? new DisplayNames([locale], { type: 'region' }).of(country) || country
      : country;
  } catch {
    return country;
  }
}

export function phoneValidationMessage(country: CountryCode, locale = 'en'): string {
  return `Enter a valid phone number for ${countryDisplayName(country, locale)}, or include the international country code.`;
}

export function phoneInputMarkup(options: {
  value: string;
  country: CountryCode;
  locale?: string;
  required?: boolean;
  describedBy?: string;
}): string {
  // Keep visible options compact in narrow donor rails. The calling prefix
  // supports phone entry while the ISO code disambiguates shared prefixes.
  const locale = COUNTRY_LABEL_LOCALE;
  const countries = [...SUPPORTED_COUNTRIES].sort((a, b) =>
    countryDisplayName(a, locale).localeCompare(countryDisplayName(b, locale), locale),
  );
  const optionHtml = countries.map((country) => {
    const callingCode = getCountryCallingCode(country);
    return `<option value="${country}" data-calling-code="+${callingCode}"${country === options.country ? ' selected' : ''}>+${callingCode} ${country}</option>`;
  }).join('');
  const selectedCallingCode = `+${getCountryCallingCode(options.country)}`;
  const required = options.required ? 'required' : '';
  const describedBy = options.describedBy || '';
  return `
    <div class="dk-phone-input" data-testid="dk-phone-input">
      <div class="dk-phone-country-picker">
        <span id="dk-phone-country-code" class="dk-phone-country-code" aria-hidden="true">${selectedCallingCode}</span>
        <select id="dk-phone-country" class="dk-phone-country" aria-label="Phone country, ${selectedCallingCode} ${options.country}">${optionHtml}</select>
      </div>
      <input id="dk-phone" type="tel" inputmode="tel" autocomplete="tel" aria-errormessage="dk-phone-error" ${required} ${describedBy} placeholder="Phone number" value="${esc(options.value)}" />
    </div>
    <span id="dk-phone-error" class="dk-field-error dk-hidden" role="alert"></span>`;
}
